/**
 * DocumentViewer Component - View documents and photos for cattle
 * Displays uploaded files with preview, download, and categorization
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Image,
  Download,
  Eye,
  X,
  File,
  FileImage,
  FileBadge,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  ExternalLink,
} from 'lucide-react';

// API base URL for file serving
const isDevelopment = import.meta.env.DEV;
const GAUSHALA_API_BASE = isDevelopment
  ? '/gaushala-service'
  : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

/**
 * Convert stored file path to full URL for viewing/downloading
 * Stored path: /uploads/cattle/{id}/photos/{filename}
 * API endpoint: /api/v1/gaushala/cattle/files/{id}/photos/{filename}
 */
const getFileUrl = (filePath: string): string => {
  if (!filePath) return '';

  // If already a full URL, return as-is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Convert stored path to API endpoint
  // /uploads/cattle/123/photos/uuid.jpg -> /api/v1/gaushala/cattle/files/123/photos/uuid.jpg
  const match = filePath.match(/\/uploads\/cattle\/(\d+)\/(photos|documents)\/(.+)$/);
  if (match) {
    const [, cattleId, subDir, filename] = match;
    return `${GAUSHALA_API_BASE}/api/v1/gaushala/cattle/files/${cattleId}/${subDir}/${filename}`;
  }

  // Fallback: prepend API base
  return `${GAUSHALA_API_BASE}${filePath}`;
};

// Document/file metadata interface
export interface DocumentFile {
  id?: number;
  fileName: string;
  fileType: 'PHOTO' | 'DOCUMENT' | string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  uploadedBy?: string;
  uploadedAt?: string;
  documentType?: string; // health_certificate, vaccination_record, purchase_document
}

// Props for the inline document grid viewer
interface DocumentGridProps {
  documents: DocumentFile[];
  cattleRecord?: {
    photoPath?: string;
    photoUrl?: string;
    vaccinationDocPath?: string;
    healthCertificatePath?: string;
    purchaseDocPath?: string;
  };
  onDownload?: (doc: DocumentFile) => void;
}

// Props for the modal viewer
interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentFile | null;
  allDocuments?: DocumentFile[];
  onNavigate?: (direction: 'prev' | 'next') => void;
}

/**
 * Helper function to determine if a file is an image
 */
const isImageFile = (file: DocumentFile): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];

  if (file.mimeType && imageMimeTypes.includes(file.mimeType.toLowerCase())) {
    return true;
  }

  if (file.fileType === 'PHOTO') {
    return true;
  }

  const fileName = file.fileName?.toLowerCase() || '';
  return imageExtensions.some(ext => fileName.endsWith(ext));
};

/**
 * Helper function to determine if a file is a PDF
 */
const isPdfFile = (file: DocumentFile): boolean => {
  if (file.mimeType === 'application/pdf') {
    return true;
  }
  return file.fileName?.toLowerCase().endsWith('.pdf') || false;
};

/**
 * Get appropriate icon for document type
 */
const getDocumentIcon = (file: DocumentFile) => {
  if (isImageFile(file)) {
    return <FileImage className="h-8 w-8 text-blue-500" />;
  }
  if (isPdfFile(file)) {
    return <FileText className="h-8 w-8 text-red-500" />;
  }

  // Check document type
  const docType = file.documentType?.toLowerCase() || file.fileName?.toLowerCase() || '';
  if (docType.includes('vaccination')) {
    return <FileBadge className="h-8 w-8 text-green-500" />;
  }
  if (docType.includes('health') || docType.includes('certificate')) {
    return <FileCheck className="h-8 w-8 text-emerald-500" />;
  }

  return <File className="h-8 w-8 text-gray-500" />;
};

/**
 * Get document category label
 */
const getDocumentCategory = (file: DocumentFile): string => {
  if (file.fileType === 'PHOTO' || isImageFile(file)) {
    return 'Photo';
  }

  const docType = file.documentType?.toLowerCase() || file.fileName?.toLowerCase() || '';
  if (docType.includes('vaccination')) {
    return 'Vaccination';
  }
  if (docType.includes('health')) {
    return 'Health Certificate';
  }
  if (docType.includes('purchase')) {
    return 'Purchase Document';
  }

  return 'Document';
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format date for display
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Document Preview Modal Component
 */
export function DocumentViewerModal({
  isOpen,
  onClose,
  document,
  allDocuments = [],
  onNavigate,
}: DocumentViewerModalProps) {
  const [zoom, setZoom] = useState(1);
  const [imageError, setImageError] = useState(false);

  if (!document) return null;

  const isImage = isImageFile(document);
  const isPdf = isPdfFile(document);
  const currentIndex = allDocuments.findIndex(d => d.filePath === document.filePath);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allDocuments.length - 1;

  const handleDownload = () => {
    if (document.filePath) {
      const fileUrl = getFileUrl(document.filePath);
      const link = window.document.createElement('a');
      link.href = fileUrl;
      link.download = document.fileName || 'download';
      link.target = '_blank';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  const handleOpenExternal = () => {
    if (document.filePath) {
      const fileUrl = getFileUrl(document.filePath);
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                {getDocumentIcon(document)}
                <span className="truncate max-w-[300px]">{document.fileName}</span>
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{getDocumentCategory(document)}</Badge>
                {document.fileSize && (
                  <span className="text-xs text-muted-foreground">
                    {formatFileSize(document.fileSize)}
                  </span>
                )}
                {document.uploadedAt && (
                  <span className="text-xs text-muted-foreground">
                    Uploaded: {formatDate(document.uploadedAt)}
                  </span>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Navigation and Zoom Controls */}
        <div className="flex items-center justify-between py-2 border-b">
          <div className="flex items-center gap-2">
            {allDocuments.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onNavigate?.('prev')}
                  disabled={!hasPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {currentIndex + 1} / {allDocuments.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onNavigate?.('next')}
                  disabled={!hasNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isImage && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground min-w-[50px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={handleOpenExternal}>
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
            <Button variant="default" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-1 overflow-auto max-h-[60vh] flex items-center justify-center bg-muted/30 rounded-lg p-4">
          {isImage && !imageError ? (
            <img
              src={getFileUrl(document.filePath)}
              alt={document.fileName}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{ transform: `scale(${zoom})` }}
              onError={() => setImageError(true)}
            />
          ) : isPdf ? (
            <div className="w-full h-full min-h-[400px]">
              <iframe
                src={getFileUrl(document.filePath)}
                className="w-full h-full min-h-[400px] rounded-lg border"
                title={document.fileName}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              {getDocumentIcon(document)}
              <p className="text-muted-foreground text-center">
                Preview not available for this file type.
                <br />
                Click "Download" or "Open" to view the file.
              </p>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
          {imageError && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <FileImage className="h-16 w-16 text-muted-foreground" />
              <p className="text-muted-foreground text-center">
                Failed to load image.
                <br />
                The file may have been moved or deleted.
              </p>
              <Button variant="outline" onClick={handleOpenExternal}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Try Opening in New Tab
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Single Document Card Component
 */
function DocumentCard({
  document,
  onView,
  onDownload,
}: {
  document: DocumentFile;
  onView: () => void;
  onDownload: () => void;
}) {
  const [imageError, setImageError] = useState(false);
  const isImage = isImageFile(document);

  return (
    <Card className="group hover:shadow-md transition-shadow overflow-hidden">
      <CardContent className="p-0">
        {/* Thumbnail Area */}
        <div
          className="relative h-32 bg-muted flex items-center justify-center cursor-pointer overflow-hidden"
          onClick={onView}
        >
          {isImage && !imageError ? (
            <img
              src={getFileUrl(document.filePath)}
              alt={document.fileName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center">
              {getDocumentIcon(document)}
              <Badge variant="secondary" className="mt-2 text-xs">
                {getDocumentCategory(document)}
              </Badge>
            </div>
          )}
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <Button variant="secondary" size="sm" className="shadow-lg">
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
          </div>
        </div>

        {/* Info Area */}
        <div className="p-3 space-y-2">
          <p className="text-sm font-medium truncate" title={document.fileName}>
            {document.fileName}
          </p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{formatFileSize(document.fileSize)}</span>
            {document.uploadedAt && (
              <span>{formatDate(document.uploadedAt).split(',')[0]}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={onView}>
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
            <Button variant="ghost" size="sm" onClick={onDownload}>
              <Download className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Main Document Grid Component - Displays all documents in categorized tabs
 */
export function DocumentGrid({ documents, cattleRecord, onDownload }: DocumentGridProps) {
  const [selectedDoc, setSelectedDoc] = useState<DocumentFile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Combine inline document paths with metadata documents
  const allDocuments: DocumentFile[] = [...documents];

  // Add cattle record document paths if they exist and aren't already in the list
  if (cattleRecord?.photoPath || cattleRecord?.photoUrl) {
    const photoPath = cattleRecord.photoPath || cattleRecord.photoUrl;
    if (photoPath && !allDocuments.some(d => d.filePath === photoPath)) {
      allDocuments.push({
        fileName: 'Cattle Photo',
        fileType: 'PHOTO',
        filePath: photoPath,
        documentType: 'photo',
      });
    }
  }

  if (cattleRecord?.vaccinationDocPath) {
    if (!allDocuments.some(d => d.filePath === cattleRecord.vaccinationDocPath)) {
      allDocuments.push({
        fileName: 'Vaccination Record',
        fileType: 'DOCUMENT',
        filePath: cattleRecord.vaccinationDocPath,
        documentType: 'vaccination_record',
      });
    }
  }

  if (cattleRecord?.healthCertificatePath) {
    if (!allDocuments.some(d => d.filePath === cattleRecord.healthCertificatePath)) {
      allDocuments.push({
        fileName: 'Health Certificate',
        fileType: 'DOCUMENT',
        filePath: cattleRecord.healthCertificatePath,
        documentType: 'health_certificate',
      });
    }
  }

  if (cattleRecord?.purchaseDocPath) {
    if (!allDocuments.some(d => d.filePath === cattleRecord.purchaseDocPath)) {
      allDocuments.push({
        fileName: 'Purchase Document',
        fileType: 'DOCUMENT',
        filePath: cattleRecord.purchaseDocPath,
        documentType: 'purchase_document',
      });
    }
  }

  // Categorize documents
  const photos = allDocuments.filter(d => d.fileType === 'PHOTO' || isImageFile(d));
  const healthDocs = allDocuments.filter(d => {
    const docType = d.documentType?.toLowerCase() || d.fileName?.toLowerCase() || '';
    return (docType.includes('health') || docType.includes('certificate')) && !isImageFile(d);
  });
  const vaccinationDocs = allDocuments.filter(d => {
    const docType = d.documentType?.toLowerCase() || d.fileName?.toLowerCase() || '';
    return docType.includes('vaccination') && !isImageFile(d);
  });
  const purchaseDocs = allDocuments.filter(d => {
    const docType = d.documentType?.toLowerCase() || d.fileName?.toLowerCase() || '';
    return docType.includes('purchase') && !isImageFile(d);
  });
  const otherDocs = allDocuments.filter(d => {
    const docType = d.documentType?.toLowerCase() || d.fileName?.toLowerCase() || '';
    return (
      d.fileType !== 'PHOTO' &&
      !isImageFile(d) &&
      !docType.includes('health') &&
      !docType.includes('certificate') &&
      !docType.includes('vaccination') &&
      !docType.includes('purchase')
    );
  });

  const handleView = (doc: DocumentFile) => {
    setSelectedDoc(doc);
    setIsModalOpen(true);
  };

  const handleDownload = (doc: DocumentFile) => {
    if (onDownload) {
      onDownload(doc);
    } else if (doc.filePath) {
      const fileUrl = getFileUrl(doc.filePath);
      const link = window.document.createElement('a');
      link.href = fileUrl;
      link.download = doc.fileName || 'download';
      link.target = '_blank';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (!selectedDoc) return;
    const currentIndex = allDocuments.findIndex(d => d.filePath === selectedDoc.filePath);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < allDocuments.length) {
      setSelectedDoc(allDocuments[newIndex]);
    }
  };

  const renderDocumentGrid = (docs: DocumentFile[], emptyMessage: string) => {
    if (docs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <FileText className="h-12 w-12 mb-4 opacity-50" />
          <p>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {docs.map((doc, index) => (
          <DocumentCard
            key={doc.id || doc.filePath || index}
            document={doc}
            onView={() => handleView(doc)}
            onDownload={() => handleDownload(doc)}
          />
        ))}
      </div>
    );
  };

  if (allDocuments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FileText className="h-16 w-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">No Documents Uploaded</p>
        <p className="text-sm">Documents and photos will appear here once uploaded.</p>
      </div>
    );
  }

  return (
    <>
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger value="all" className="text-xs sm:text-sm">
            <FileText className="h-4 w-4 mr-1 hidden sm:inline" />
            All ({allDocuments.length})
          </TabsTrigger>
          <TabsTrigger value="photos" className="text-xs sm:text-sm">
            <Image className="h-4 w-4 mr-1 hidden sm:inline" />
            Photos ({photos.length})
          </TabsTrigger>
          <TabsTrigger value="health" className="text-xs sm:text-sm">
            <FileCheck className="h-4 w-4 mr-1 hidden sm:inline" />
            Health ({healthDocs.length})
          </TabsTrigger>
          <TabsTrigger value="vaccination" className="text-xs sm:text-sm">
            <FileBadge className="h-4 w-4 mr-1 hidden sm:inline" />
            Vaccination ({vaccinationDocs.length})
          </TabsTrigger>
          <TabsTrigger value="other" className="text-xs sm:text-sm">
            <File className="h-4 w-4 mr-1 hidden sm:inline" />
            Other ({purchaseDocs.length + otherDocs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {renderDocumentGrid(allDocuments, 'No documents available')}
        </TabsContent>

        <TabsContent value="photos">
          {renderDocumentGrid(photos, 'No photos uploaded')}
        </TabsContent>

        <TabsContent value="health">
          {renderDocumentGrid(healthDocs, 'No health certificates uploaded')}
        </TabsContent>

        <TabsContent value="vaccination">
          {renderDocumentGrid(vaccinationDocs, 'No vaccination records uploaded')}
        </TabsContent>

        <TabsContent value="other">
          {renderDocumentGrid([...purchaseDocs, ...otherDocs], 'No other documents uploaded')}
        </TabsContent>
      </Tabs>

      {/* Document Preview Modal */}
      <DocumentViewerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        document={selectedDoc}
        allDocuments={allDocuments}
        onNavigate={handleNavigate}
      />
    </>
  );
}

export default DocumentGrid;
